import { useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Block, BlockFooter, Icon, List, ListItem } from "framework7-react";
import { requests } from "../../constants/requests";
import { vibrate } from "../../hooks/use-haptics";
import { useIsPending } from "../../hooks/use-is-pending";
import { useToast } from "../../hooks/use-toast";
import { createPayment, selectMe } from "../../redux/reducer/app-slice";
import { getGroupTotals } from "../../utils/balances";
import { confirmDialog } from "../../utils/confirm-dialog";
import { formatCents } from "../../utils/format";
import { queuedText } from "../../utils/outbox";
import { SETTLED_TEXT, describeSettlement } from "../../utils/settlement-text";
import { getSettlements } from "../../utils/settlements";
import SettlementActions from "./settlement-actions";

const Settlement = ({ group, active = true }) => {
  const participants = group?.participants ?? [];
  const me = useSelector(selectMe);
  const dispatch = useDispatch();
  const { showInfo, showError } = useToast();
  const isSaving = useIsPending(requests.savePayment);
  const dialogRef = useRef(null);
  const activeRef = useRef(active);
  activeRef.current = active;

  useEffect(() => {
    if (!active) dialogRef.current?.close();
  }, [active]);

  const settlements = getSettlements(participants).map((s, index) => ({
    ...s,
    ...describeSettlement(s, participants, me?.id),
    key: `${s.fromId}-${s.toId}-${index}`,
  }));

  const markAsPaid = (settlement) => {
    if (isSaving || !active) return;
    dialogRef.current = confirmDialog({
      title: "Zahlung erfassen",
      text: settlement.question,
      confirmText: "Als bezahlt markieren",
      cssClass: "settlement-dialog",
      onClosed: () => {
        dialogRef.current = null;
      },
      onConfirm: () => {
        if (!activeRef.current) return;
        dispatch(
          createPayment(
            {
              fromId: settlement.fromId,
              toId: settlement.toId,
              amount: settlement.amount,
            },
            (group, { queued = false } = {}) => {
              vibrate();
              showInfo(
                queued ? queuedText("Zahlung erfasst") : "Zahlung erfasst",
              );
            },
            showError,
          ),
        );
      },
    });
  };

  return (
    <div className="settlement">
      {settlements.length === 0 ? (
        <Block key="empty" strong inset className="settlement-empty">
          <Icon
            ios="f7:checkmark_circle_fill"
            md="material:check_circle"
            size={20}
          />
          <span>{SETTLED_TEXT}</span>
        </Block>
      ) : (
        <div key="list">
          <List
            className="settlement-list"
            strong
            inset
            dividersIos
            dividersMd
          >
            {settlements.map((s) => (
              <ListItem
                key={s.key}
                link
                disabled={isSaving}
                className={s.involvesMe ? "settlement-mine" : undefined}
                title={s.title}
                after={formatCents(s.amount)}
                onClick={() => markAsPaid(s)}
              />
            ))}
          </List>
          <BlockFooter className="settlement-hint">
            Tippe auf eine Überweisung, sobald sie erledigt ist.
          </BlockFooter>
        </div>
      )}
      {getGroupTotals(group).hasEntries && <SettlementActions group={group} />}
    </div>
  );
};

export default Settlement;
